import React, { useState, useEffect, useRef } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  FileAudio,
  FileVideo,
  Loader2,
  Moon,
  Music,
  RefreshCw,
  Sun,
  UploadCloud,
  Video,
  X,
} from 'lucide-react';
import logo from './logo.svg';
import logoDark from './logo_dark.svg';
import ApiService from './ApiService';
import { useTheme } from './ThemeContext';
const UploadingPage = () => {
  const { isDark, toggle: toggleTheme } = useTheme();
  const [mediaType, setMediaType] = useState('video');
  const [files, setFiles] = useState([]);
  const [commands, setCommands] = useState({ processing: [], attribute: [] });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeTaskCount, setActiveTaskCount] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const claimedFileIds = useRef(new Set());
  const cancelledFileIds = useRef(new Set());
  const uploadControllers = useRef(new Map());
  const nextTaskNumber = useRef(1);
  const uploadQueue = useRef(Promise.resolve());
  const [formData, setFormData] = useState({
    processingTaskId: '',
    attributeTaskId: '',
    seriesNumber: '',
    seasonNumber: '',
    animeId: '',
    quality: 'QHD',
    notifyType: 'Notify',
    uploaderId: '',
    note: '',
    priority: 5000  // Added default priority value
  });

  // Fetch available commands on mount
  useEffect(() => {
    fetchCommands();
  }, []);

  const fetchCommands = async () => {
    try {
      const data = await ApiService.getCommands();
      setCommands(data);
    } catch (error) {
      console.error('Failed to fetch commands:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      addToQueue(file);
    }
    // Allow selecting the same file again after it has been removed.
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files && event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      addToQueue(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const addToQueue = (file) => {
    const createdAt = Date.now();
    const newFile = {
      id: `${createdAt}-${Math.random().toString(36).slice(2)}`,
      name: file.name,
      status: 'In queue',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: new Date().toLocaleDateString('en-GB').split('/').join('.'),
      createdAt,
      mediaType,
      progress: 0,
      file
    };
    // Newest items are displayed first.
    setFiles(prev => [newFile, ...prev]);
    setFeedback(null);
  };

  const removeFromQueue = (fileId) => {
    const removedFile = files.find(file => file.id === fileId);
    if (removedFile?.file === selectedFile) setSelectedFile(null);
    setFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const updateFileStatus = (fileId, status, progress = 0) => {
    setFiles(prev => prev.map(file => {
      if (file.id === fileId) {
        return { ...file, status, progress };
      }
      return file;
    }));
  };

  const uploadFile = async (fileToUpload, submissionData) => {
    if (cancelledFileIds.current.has(fileToUpload.id)) {
      updateFileStatus(fileToUpload.id, 'Cancelled');
      return 'cancelled';
    }

    updateFileStatus(fileToUpload.id, 'Uploading', 0);
    const controller = new AbortController();
    uploadControllers.current.set(fileToUpload.id, controller);

    const formPayload = new FormData();
    formPayload.append('file', fileToUpload.file);
    formPayload.append('contentType', fileToUpload.mediaType.toUpperCase());
    formPayload.append('processingTaskId', submissionData.processingTaskId);
    formPayload.append('attributeTaskId', submissionData.attributeTaskId);
    formPayload.append('hookUid', 'animix');
    formPayload.append('priority', parseInt(submissionData.priority, 10));
    formPayload.append('hookPayload', JSON.stringify({
      seriesNumber: submissionData.seriesNumber,
      seasonNumber: submissionData.seasonNumber,
      quality: submissionData.quality,
      notifyType: submissionData.notifyType,
      uploaderId: submissionData.uploaderId,
      animeId: submissionData.animeId,
    }));

    formPayload.onProgress = (progress) => {
      updateFileStatus(fileToUpload.id, 'Uploading', progress);
    };

    try {
      await ApiService.uploadFile(formPayload, { signal: controller.signal });
      if (cancelledFileIds.current.has(fileToUpload.id)) {
        updateFileStatus(fileToUpload.id, 'Cancelled');
        return 'cancelled';
      }
      updateFileStatus(fileToUpload.id, 'Encoding', 100);
      return true;
    } catch (error) {
      if (cancelledFileIds.current.has(fileToUpload.id)) {
        updateFileStatus(fileToUpload.id, 'Cancelled');
        return 'cancelled';
      }
      console.error('Upload failed:', error);
      updateFileStatus(fileToUpload.id, 'Error');
      return false;
    } finally {
      uploadControllers.current.delete(fileToUpload.id);
    }
  };

  const runTask = async (taskNumber, taskFiles, submissionData) => {
    let succeeded = 0;
    let failed = 0;
    let cancelled = 0;

    // Files in one task retain their shared configuration and are uploaded in order.
    for (const taskFile of taskFiles) {
      const wasSuccessful = await uploadFile(taskFile, submissionData);
      if (wasSuccessful === 'cancelled') cancelled += 1;
      else if (wasSuccessful) succeeded += 1;
      else failed += 1;
    }

    taskFiles.forEach(taskFile => claimedFileIds.current.delete(taskFile.id));
    setActiveTaskCount(count => Math.max(0, count - 1));
    setFeedback(
      failed
        ? {
          type: 'error',
          message: `Task #${taskNumber}: ${succeeded} submitted, ${failed} failed${cancelled ? `, ${cancelled} cancelled` : ''}.`,
        }
        : cancelled
          ? {
            type: 'progress',
            message: `Task #${taskNumber}: ${succeeded} submitted, ${cancelled} cancelled.`,
          }
        : {
          type: 'success',
          message: `Task #${taskNumber}: all ${succeeded} series submitted successfully.`,
        }
    );
  };

  const enqueueTask = (taskNumber, taskFiles, submissionData) => {
    // There is one global upload chain. New tasks can be configured immediately,
    // but no file starts uploading until every file queued before it has finished.
    uploadQueue.current = uploadQueue.current.then(
      () => runTask(taskNumber, taskFiles, submissionData),
      () => runTask(taskNumber, taskFiles, submissionData)
    );
  };

  const handleCreate = () => {
    const queuedFiles = files.filter(
      file => file.status === 'In queue' && !claimedFileIds.current.has(file.id)
    );
    if (!queuedFiles.length) return;

    // Every task gets an immutable configuration snapshot. Later form changes only
    // affect the next task, even while this one is still uploading.
    const submissionData = { ...formData };
    const taskNumber = nextTaskNumber.current;
    nextTaskNumber.current += 1;

    queuedFiles.forEach(file => claimedFileIds.current.add(file.id));
    setFiles(currentFiles => currentFiles.map(file => (
      claimedFileIds.current.has(file.id) && file.status === 'In queue'
        ? { ...file, status: 'Waiting', taskNumber, taskConfig: submissionData }
        : file
    )));
    setSelectedFile(null);
    setActiveTaskCount(count => count + 1);
    setFeedback({
      type: 'progress',
      message: `Task #${taskNumber} started with ${queuedFiles.length} series. You can configure and start another task now.`,
    });

    enqueueTask(taskNumber, queuedFiles, submissionData);
  };

  const handleRetry = (fileId) => {
    const failedFile = files.find(
      file => file.id === fileId && (file.status === 'Error' || file.status === 'Cancelled')
    );
    if (!failedFile?.taskConfig || claimedFileIds.current.has(fileId)) return;

    cancelledFileIds.current.delete(fileId);
    claimedFileIds.current.add(fileId);
    setActiveTaskCount(count => count + 1);
    setFeedback({
      type: 'progress',
      message: `Retrying ${failedFile.name} in task #${failedFile.taskNumber}.`,
    });

    enqueueTask(failedFile.taskNumber, [failedFile], failedFile.taskConfig);
  };

  const handleCancel = (fileId) => {
    const fileToCancel = files.find(
      file => file.id === fileId && (file.status === 'Waiting' || file.status === 'Uploading')
    );
    if (!fileToCancel || cancelledFileIds.current.has(fileId)) return;

    cancelledFileIds.current.add(fileId);
    uploadControllers.current.get(fileId)?.abort();
    updateFileStatus(fileId, 'Cancelled');
    setFeedback({
      type: 'progress',
      message: `${fileToCancel.name} was cancelled.`,
    });
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'encoding': return '#10B981';
      case 'uploading': return '#3B82F6';
      case 'cancelled': return '#F59E0B';
      case 'error': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const acceptedFileTypes = {
    video: '.mp4,.mkv,.avi,.mov,.wmv,.flv,.webm,.m4v,.mpeg,.mpg',
    audio: '.mp3,.wav,.aac,.m4a,.ogg,.flac,.wma,.aiff'
  };

  const queuedCount = files.filter(file => file.status === 'In queue').length;
  const isFormComplete = Boolean(formData.processingTaskId && formData.attributeTaskId);
  const canCreate = queuedCount > 0 && isFormComplete;
  const fieldClass = `w-full rounded-lg border px-3.5 py-2.5 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${isDark
    ? 'border-gray-700 bg-gray-800 text-white placeholder:text-gray-500'
    : 'border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400'
    }`;

  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-gray-950 text-white' : 'bg-slate-50 text-slate-900'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-20 border-b backdrop-blur ${isDark ? 'bg-gray-900/90 border-gray-800' : 'bg-white/90 border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <img className="max-h-10" src={isDark ? logoDark : logo} alt="Streamline Logo" />

          <div className="flex items-center gap-3 sm:gap-5">
            <div className={`inline-flex rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'} p-1`}>
              <button
                onClick={() => setMediaType('video')}
                aria-pressed={mediaType === 'video'}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all ${mediaType === 'video'
                  ? `${isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'} shadow`
                  : `${isDark ? 'text-gray-300' : 'text-gray-600'}`
                  }`}
              >
                <Video size={16} />
                <span className="hidden sm:inline">Video</span>
              </button>
              <button
                onClick={() => setMediaType('audio')}
                aria-pressed={mediaType === 'audio'}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-all ${mediaType === 'audio'
                  ? `${isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'} shadow`
                  : `${isDark ? 'text-gray-300' : 'text-gray-600'}`
                  }`}
              >
                <Music size={16} />
                <span className="hidden sm:inline">Audio</span>
              </button>
            </div>

            <button
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
              title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
              className={`p-2.5 rounded-lg transition-colors ${isDark ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-7">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-500">New task</p>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight">Upload media</h1>
          <p className={`mt-2 text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
            Add series and configure independent tasks. Files upload one at a time in queue order.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 items-start">
          {/* Queue Section */}
          <section className="lg:col-span-2 lg:sticky lg:top-24">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">Queue</h2>
                <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  Newest files appear first
                </p>
              </div>
              <div className="flex items-center gap-2">
                {activeTaskCount > 0 && (
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                    <Loader2 size={12} className="animate-spin" /> {activeTaskCount} {activeTaskCount === 1 ? 'task' : 'tasks'} active
                  </span>
                )}
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-slate-200 text-slate-600'}`}>
                  {queuedCount} waiting
                </span>
              </div>
            </div>

            <div className="space-y-3 max-h-[calc(100vh-12rem)] overflow-y-auto pr-1">
              {files.length === 0 && (
                <div className={`rounded-xl border-2 border-dashed px-6 py-12 text-center ${isDark ? 'border-gray-800 bg-gray-900/50' : 'border-slate-200 bg-white'}`}>
                  <div className={`mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-slate-100 text-slate-500'}`}>
                    <Clock3 size={21} />
                  </div>
                  <p className="font-medium">Queue is empty</p>
                  <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Choose or drop a file to get started.</p>
                </div>
              )}
              {files.map((file) => (
                <div
                  key={file.id}
                  className={`rounded-xl border p-4 transition-all ${file.status === 'Uploading'
                    ? isDark ? 'border-blue-500/50 bg-blue-500/5 shadow-lg shadow-blue-950/20' : 'border-blue-200 bg-blue-50/40 shadow-sm'
                    : isDark ? 'border-gray-800 bg-gray-900' : 'border-slate-200 bg-white shadow-sm'
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-slate-100 text-slate-600'}`}>
                      {file.mediaType === 'video' ? <FileVideo size={18} /> : <FileAudio size={18} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-medium flex-1 min-w-0 truncate" title={file.name}>{file.name}</span>
                        <span
                          className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                          style={{ backgroundColor: `${getStatusColor(file.status)}15`, color: getStatusColor(file.status) }}
                        >
                          {file.status}
                        </span>
                      </div>

                      {file.status === 'Uploading' && (
                        <div className="mt-3">
                          <div className="mb-1.5 flex items-center justify-between text-xs text-blue-500">
                            <span>Uploading</span>
                            <span>{file.progress || 0}%</span>
                          </div>
                          <div className={`h-1.5 overflow-hidden rounded-full ${isDark ? 'bg-gray-700' : 'bg-blue-100'}`}>
                            <div
                              className="h-full bg-blue-500 rounded-full transition-all duration-300"
                              style={{ width: `${file.progress || 0}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="mt-3 flex items-center justify-between gap-3">
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                          {file.taskNumber ? `Task #${file.taskNumber} · ` : ''}{file.date} at {file.time}
                        </span>
                        {file.status === 'In queue' && (
                          <button
                            onClick={() => removeFromQueue(file.id)}
                            aria-label={`Remove ${file.name} from queue`}
                            title="Remove from queue"
                            className={`rounded-md p-1 transition-colors ${isDark ? 'text-gray-500 hover:bg-red-500/10 hover:text-red-400' : 'text-slate-400 hover:bg-red-50 hover:text-red-500'}`}
                          >
                            <X size={16} />
                          </button>
                        )}
                        {(file.status === 'Waiting' || file.status === 'Uploading') && (
                          <button
                            onClick={() => handleCancel(file.id)}
                            aria-label={`Cancel ${file.name}`}
                            className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors ${isDark ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                          >
                            <X size={13} />
                            Cancel
                          </button>
                        )}
                        {(file.status === 'Error' || file.status === 'Cancelled') && (
                          <button
                            onClick={() => handleRetry(file.id)}
                            disabled={claimedFileIds.current.has(file.id)}
                            aria-label={`Retry ${file.name}`}
                            className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors ${claimedFileIds.current.has(file.id)
                              ? 'cursor-wait opacity-50'
                              : isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100'
                              }`}
                          >
                            <RefreshCw size={13} />
                            Retry
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Upload Form Section */}
          <section className="lg:col-span-3">
            <div className={`rounded-2xl border p-5 sm:p-7 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-slate-200 shadow-sm'}`}>
              <h2 className="text-xl sm:text-2xl font-semibold mb-1">
                Create {mediaType === 'video' ? 'Video' : 'Audio'} Task
              </h2>
              <p className={`mb-7 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Required fields are marked with an asterisk.</p>

              <div className="space-y-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    1. Select source file <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={`rounded-xl border-2 border-dashed p-5 text-center transition-all ${isDragging
                      ? 'border-blue-500 bg-blue-500/10 scale-[1.01]'
                      : isDark ? 'border-gray-700 bg-gray-800/40 hover:border-gray-600' : 'border-slate-200 bg-slate-50/70 hover:border-blue-300'
                      }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    <div className={`mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full ${isDragging ? 'bg-blue-500 text-white' : isDark ? 'bg-gray-700 text-gray-300' : 'bg-white text-blue-500 shadow-sm'}`}>
                      <UploadCloud size={21} />
                    </div>
                    <p className="font-medium">{isDragging ? 'Drop the file here' : 'Drag and drop a file'}</p>
                    <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      {selectedFile ? `Last added: ${selectedFile.name}` : `Supported ${mediaType} formats`}
                    </p>
                    <label className="mt-4 inline-flex cursor-pointer items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-blue-700 active:scale-[0.98] focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2">
                      Choose file
                      <input
                        type="file"
                        accept={acceptedFileTypes[mediaType]}
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="processing-task" className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      2. Processing task <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="processing-task"
                      value={formData.processingTaskId}
                      onChange={(e) => setFormData(prev => ({ ...prev, processingTaskId: e.target.value }))}
                      className={fieldClass}
                    >
                      <option value="">Select processing task</option>
                      {commands.processing.map(cmd => (
                        <option key={cmd} value={cmd}>{cmd}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="attribute-task" className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      3. Attribute task <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="attribute-task"
                      value={formData.attributeTaskId}
                      onChange={(e) => setFormData(prev => ({ ...prev, attributeTaskId: e.target.value }))}
                      className={fieldClass}
                    >
                      <option value="">Select attribute task</option>
                      {commands.attribute.map(cmd => (
                        <option key={cmd} value={cmd}>{cmd}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    4. Configure hooks
                  </label>
                  <div className="flex gap-2 mb-4">
                    <div className={`px-3 py-1.5 text-sm font-medium text-blue-500 rounded-lg border ${isDark ? 'bg-blue-500/10 border-blue-500/30' : 'bg-blue-50 border-blue-200'}`}>
                      animix
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Series number"
                      value={formData.seriesNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, seriesNumber: e.target.value }))}
                      className={fieldClass}
                    />
                    <input
                      type="text"
                      placeholder="Season number"
                      value={formData.seasonNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, seasonNumber: e.target.value }))}
                      className={fieldClass}
                    />
                    <input
                      type="text"
                      placeholder="Anime Id"
                      value={formData.animeId}
                      onChange={(e) => setFormData(prev => ({ ...prev, animeId: e.target.value }))}
                      className={fieldClass}
                    />
                    <select
                      value={formData.quality}
                      onChange={(e) => setFormData(prev => ({ ...prev, quality: e.target.value }))}
                      className={fieldClass}
                    >
                      <option value="HD">HD</option>
                      <option value="FULL_HD">FULL_HD</option>
                      <option value="QHD">QHD</option>
                      <option value="ULTRA_HD">ULTRA_HD</option>
                    </select>
                    <select
                      value={formData.notifyType}
                      onChange={(e) => setFormData(prev => ({ ...prev, notifyType: e.target.value }))}
                      className={fieldClass}
                    >
                      <option value="Notify">Notify</option>
                      <option value="Silent">Silent</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Uploader Id"
                      value={formData.uploaderId}
                      onChange={(e) => setFormData(prev => ({ ...prev, uploaderId: e.target.value }))}
                      className={fieldClass}
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="priority" className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Priority
                    </label>
                    <span className={`rounded-md px-2 py-1 text-xs font-semibold tabular-nums ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-slate-100 text-slate-600'}`}>
                      {formData.priority}
                    </span>
                  </div>
                  <input
                    id="priority"
                    type="range"
                    min="1"
                    max="10000"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className={`flex justify-between text-xs mt-1.5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                    <span>1</span>
                    <span>5000</span>
                    <span>10000</span>
                  </div>
                </div>

                <div>
                  <textarea
                    placeholder="Note (optional)"
                    value={formData.note}
                    onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                    className={`${fieldClass} h-24 resize-none`}
                  />
                </div>

                {feedback && (
                  <div
                    role="status"
                    aria-live="polite"
                    className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${feedback.type === 'success'
                      ? isDark ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : feedback.type === 'error'
                        ? isDark ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-red-200 bg-red-50 text-red-700'
                        : isDark ? 'border-blue-500/30 bg-blue-500/10 text-blue-300' : 'border-blue-200 bg-blue-50 text-blue-700'
                      }`}
                  >
                    {feedback.type === 'success'
                      ? <CheckCircle2 className="mt-0.5 flex-shrink-0" size={18} />
                      : feedback.type === 'error'
                        ? <AlertCircle className="mt-0.5 flex-shrink-0" size={18} />
                        : <Loader2 className="mt-0.5 flex-shrink-0 animate-spin" size={18} />}
                    <span>{feedback.message}</span>
                  </div>
                )}

                <button
                  onClick={handleCreate}
                  disabled={!canCreate}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 font-semibold text-white shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${canCreate
                    ? 'bg-blue-600 hover:bg-blue-700 active:scale-[0.99] active:shadow-inner'
                    : isDark ? 'cursor-not-allowed bg-blue-900/60 text-blue-300' : 'cursor-not-allowed bg-blue-300'
                    }`}
                >
                  <UploadCloud size={18} />
                  Create task with {queuedCount} {queuedCount === 1 ? 'series' : 'series'}
                </button>
                {!canCreate && (
                  <p className={`-mt-3 text-center text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                    {queuedCount === 0 ? 'Add at least one file to the queue' : 'Select both processing tasks to continue'}
                  </p>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default UploadingPage;
